import { getAccessToken } from '../authentication/supabaseService';

// ---------------------------------------------------------------------------
// Shared JSONB shapes (mirror the spec's data model — kept in sync with the
// server's menuChange service types).
// ---------------------------------------------------------------------------

export interface TemplateWeek {
  pos: number;
  label: string;
  tasks: { label: string }[];
}

export interface MenuChangeTemplate {
  id: string;
  name: string;
  weeks: TemplateWeek[];
  createdAt: string;
  updatedAt: string;
}

export type TaskStatus = 'open' | 'done' | 'not_done';

export interface MenuChangeTask {
  taskId: string;
  label: string;
  assigneeUserId: string | null;
  assigneeName?: string | null; // resolved server-side in read responses
  status: TaskStatus;
  completedOn: string | null;
  statusChangedAt: string | null;
  statusChangedBy: string | null;
  done: boolean;
  doneBy: string | null;
  doneAt: string | null;
}

// Week status is derived server-side (never stored).
export type WeekStatus = 'done' | 'not_done' | 'inprogress' | 'blocked' | 'upcoming';

export interface MenuChangeWeek {
  pos: number;
  label: string;
  status: WeekStatus;
  window: { start: string; end: string };
  tasks: MenuChangeTask[];
}

export type RolloutStatus = 'active' | 'complete' | 'cancelled';

// Card / dashboard-level derived status per restaurant.
export type CardStatus = 'inactive' | 'scheduled' | 'live' | 'blocked' | 'complete' | 'incomplete';

export interface AssignableUser {
  id: string;
  name: string; // falls back to the email when the user has no name
  email: string;
}

export interface TaskReminderState {
  eligible: boolean;
  reason: 'unassigned' | 'recipient_unavailable' | 'not_overdue' | 'done' | 'cancelled' | 'cooldown' | null;
  dueDate: string;
  daysOverdue: number;
  recipient: AssignableUser | null;
  lastSentAt: string | null;
  nextAllowedAt: string | null;
}

export class MenuChangeRequestError extends Error {
  constructor(public status: number, statusText: string) {
    super(`Menu change request failed: ${status} ${statusText}`);
  }
}

// Full rollout detail returned by rollout / restaurant-detail endpoints.
export interface RolloutDetail {
  id: string;
  restaurantId: string;
  name: string;
  goLiveDate: string; // plain date (YYYY-MM-DD)
  startDate: string;
  status: RolloutStatus;
  hasUnfinishedTasks: boolean;
  currentWeekPos: number;
  numWeeks: number;
  progress: number;
  weeks: MenuChangeWeek[];
  completedAt: string | null;
  cancelledAt: string | null;
}

export interface RolloutSummary {
  id: string;
  name: string;
  goLiveDate: string;
  status: RolloutStatus;
  hasUnfinishedTasks: boolean;
  progress: number;
  completedAt: string | null;
  cancelledAt: string | null;
}

export interface RestaurantDetailResponse {
  active?: RolloutDetail;
  history: RolloutDetail[];
  assignableUsers: AssignableUser[];
}

export interface DashboardEntry {
  restaurantId: string;
  restaurantName: string;
  // Optional restaurant logo (public URL) — null when none is uploaded.
  restaurantLogoUrl: string | null;
  status: CardStatus;
  hasUnfinishedTasks: boolean;
  latestRollout?: RolloutSummary;
  activeRollout?: {
    id: string;
    name: string;
    goLiveDate: string;
    startDate: string;
    currentWeekPos: number;
    numWeeks: number;
    progress: number;
    currentWeekTasks: MenuChangeTask[];
  };
}

export interface MyTaskEntry {
  restaurantId: string;
  restaurantName: string;
  restaurantLogoUrl: string | null;
  rolloutId: string;
  rolloutName: string;
  weekPos: number;
  weekLabel: string;
  weekStatus: WeekStatus;
  task: MenuChangeTask;
}

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

export interface TemplateCreateRequest {
  name: string;
  weeks: TemplateWeek[];
}

export interface TemplateUpdateRequest {
  name?: string;
  weeks?: TemplateWeek[];
}

export interface RolloutCreateRequest {
  restaurantId: string;
  templateId: string;
  name: string;
  goLiveDate: string;
}

export interface RolloutUpdateRequest {
  name?: string;
  goLiveDate?: string;
}

export interface TaskCreateRequest {
  label: string;
  assigneeUserId?: string | null;
}

export interface TaskUpdateRequest {
  status?: TaskStatus;
  completedOn?: string;
  label?: string;
  assigneeUserId?: string | null;
}

// ---------------------------------------------------------------------------
// Fetch helper
// ---------------------------------------------------------------------------

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const res = await fetch(`/api/menu-change${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init?.headers || {}) },
  });
  if (!res.ok) throw new MenuChangeRequestError(res.status, res.statusText);
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ---------------------------------------------------------------------------
// Templates (account managers)
// ---------------------------------------------------------------------------

export function fetchTemplates(): Promise<MenuChangeTemplate[]> {
  return request<MenuChangeTemplate[]>('/templates', { method: 'GET' });
}

export function createTemplate(body: TemplateCreateRequest): Promise<MenuChangeTemplate> {
  return request<MenuChangeTemplate>('/templates', { method: 'POST', body: JSON.stringify(body) });
}

export function updateTemplate(id: string, body: TemplateUpdateRequest): Promise<MenuChangeTemplate> {
  return request<MenuChangeTemplate>(`/templates/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}

export function deleteTemplate(id: string): Promise<{ success: boolean }> {
  return request<{ success: boolean }>(`/templates/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Dashboard / detail (admin + permitted, scoped staff)
// ---------------------------------------------------------------------------

export function fetchDashboard(): Promise<DashboardEntry[]> {
  return request<DashboardEntry[]>('/dashboard', { method: 'GET' });
}

export function fetchRestaurantDetail(restaurantId: string): Promise<RestaurantDetailResponse> {
  return request<RestaurantDetailResponse>(`/restaurant/${restaurantId}`, { method: 'GET' });
}

export function fetchMyTasks(): Promise<MyTaskEntry[]> {
  return request<MyTaskEntry[]>('/my-tasks', { method: 'GET' });
}

export function fetchReminderStates(rolloutId: string): Promise<{ tasks: Record<string, TaskReminderState> }> {
  return request(`/rollouts/${encodeURIComponent(rolloutId)}/reminders`);
}

export function sendTaskReminder(rolloutId: string, taskId: string, requestId: string): Promise<{ status: 'sent' | 'already_sent'; reminder: TaskReminderState }> {
  return request(`/rollouts/${encodeURIComponent(rolloutId)}/tasks/${encodeURIComponent(taskId)}/reminder`, {
    method: 'POST', body: JSON.stringify({ requestId }),
  });
}

// ---------------------------------------------------------------------------
// Rollouts (admin + permitted, scoped staff)
// ---------------------------------------------------------------------------

export function createRollout(body: RolloutCreateRequest): Promise<RolloutDetail> {
  return request<RolloutDetail>('/rollouts', { method: 'POST', body: JSON.stringify(body) });
}

export function updateRollout(id: string, body: RolloutUpdateRequest): Promise<RolloutDetail> {
  return request<RolloutDetail>(`/rollouts/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export function cancelRollout(id: string): Promise<RolloutDetail> {
  return request<RolloutDetail>(`/rollouts/${id}/cancel`, { method: 'POST' });
}

export function renameWeek(rolloutId: string, pos: number, label: string): Promise<RolloutDetail> {
  return request<RolloutDetail>(`/rollouts/${rolloutId}/weeks/${pos}`, {
    method: 'PATCH',
    body: JSON.stringify({ label }),
  });
}

export function addTask(rolloutId: string, pos: number, body: TaskCreateRequest): Promise<RolloutDetail> {
  return request<RolloutDetail>(`/rollouts/${rolloutId}/weeks/${pos}/tasks`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updateTask(rolloutId: string, taskId: string, body: TaskUpdateRequest): Promise<RolloutDetail> {
  return request<RolloutDetail>(`/rollouts/${rolloutId}/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function deleteTask(rolloutId: string, taskId: string): Promise<RolloutDetail> {
  return request<RolloutDetail>(`/rollouts/${rolloutId}/tasks/${taskId}`, { method: 'DELETE' });
}
